import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogIn, AlertTriangle } from "lucide-react";
import { apiRequest, setAuthToken, setJustLoggedIn } from "./queryClient";
import { queryClient } from "./queryClient";

interface SessionContextType {
  showSessionExpired: () => void;
  isSessionExpired: boolean;
}

const SessionContext = createContext<SessionContextType | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showSessionExpired = useCallback(() => {
    console.log("[session] Session expired - showing login modal");
    setIsSessionExpired(true);
    setError(null);
    setPassword("");
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setError(null);

    try {
      const res = await apiRequest("POST", "/api/auth/login", { username, password });
      const data = await res.json();
      
      if (data?.user) {
        queryClient.setQueryData(["/api/auth/me"], { user: data.user });
      }
      if (data?.token) {
        setAuthToken(data.token);
      }
      setJustLoggedIn();
      setIsSessionExpired(false);
      setPassword("");
      
      queryClient.invalidateQueries();
    } catch (err) {
      setError("Feil brukernavn eller passord");
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <SessionContext.Provider value={{ showSessionExpired, isSessionExpired }}>
      {children}
      
      <Dialog open={isSessionExpired} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Sesjonen har utløpt
            </DialogTitle>
            <DialogDescription>
              Logg inn igjen for å fortsette der du var.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleLogin} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="session-username">Brukernavn</Label>
              <Input
                id="session-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Skriv inn brukernavn"
                autoComplete="username"
                data-testid="input-session-username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="session-password">Passord</Label>
              <Input
                id="session-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Skriv inn passord"
                autoComplete="current-password"
                data-testid="input-session-password"
              />
            </div>
            
            {error && (
              <p className="text-sm text-destructive" data-testid="text-session-error">
                {error}
              </p>
            )}
            
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoggingIn || !username || !password}
              data-testid="button-session-login"
            >
              {isLoggingIn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logger inn...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Logg inn
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}

let globalShowSessionExpired: (() => void) | null = null;

export function setGlobalSessionHandler(handler: () => void) {
  globalShowSessionExpired = handler;
}

export function triggerSessionExpired() {
  if (globalShowSessionExpired) {
    globalShowSessionExpired();
  }
}
